/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import compose from 'lodash/fp/compose';
import { withNamespaces } from 'react-i18next';
import defaultsDeep from 'lodash/defaultsDeep';
import { COMMONS as dict } from '../../../utils/dictionarykeys';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { priceCss } from './index.styles';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

/**
 * Takes a price value and formats it with commas as thousands separators
 * @param {number} price - positive numbers
 * @returns {string} formatted price, e.g: 1,000. 1,000,000.
 */
export const formatPriceToCurrencyString = price => {
  const setThousands = /\d(?=(\d{3})+\.)/g;
  return price.toFixed(2).replace(setThousands, '$&,').slice(0, -3);
};

/**
 * @param {number} amount - positive numbers
 * @param {culture} string
 * @returns {string} formatted number for culture
 */
export const formatPriceWithBrowserFormatter = (amount, culture) => {
  let formattedPrice = amount;
  //need try/catch because culture may be absent of wrong
  try {
    formattedPrice = new Intl.NumberFormat(culture).format(amount);
  } catch {}
  return formattedPrice;
};

/**
 * Evaluate if a value fulfills the critera of a "valid" price number
 * @param {*} value
 * @returns {bool}
 */
export const isValidPrice = value =>
  typeof value === 'number' && Math.floor(value) >= 1;

const defaultPriceData = {
  Currency: '',
  Price: 0,
  PriceUnit: '',
  ShowCurrencySymbolAfter: false,
};

export class PriceGenerator extends SafePureComponent {
  getPrice = priceValue => {
    const { t, sitecoreContext } = this.props;
    const { Currency, Price, PriceUnit, ShowCurrencySymbolAfter } = priceValue;
    const currency = t(Currency);
    const priceUnit = t(PriceUnit);
    const culture = get(sitecoreContext, 'language', 'en-US');
    const formatPrice =
      Intl && Intl.NumberFormat
        ? formatPriceWithBrowserFormatter(Price, culture)
        : formatPriceToCurrencyString(Price);

    if (ShowCurrencySymbolAfter) {
      return `${formatPrice} ${currency} ${priceUnit}`;
    }

    return `${currency} ${formatPrice} ${priceUnit}`;
  };

  safeRender() {
    const { props } = this;
    const { t, type = null } = props;
    const price = defaultsDeep(props.price, defaultPriceData);
    const { Price } = price;

    if (!isValidPrice(Price)) {
      return t('PleaseCallUs');
    }

    if (type === 'listing') {
      return (
        <React.Fragment>
          <span>{`${t(dict.FROM)}`}&nbsp;</span>
          <span css={priceCss}>{this.getPrice(price)}</span>
        </React.Fragment>
      );
    }

    return `${t(dict.FROM)} ${this.getPrice(price)}`;
  }
}

PriceGenerator.displayName = 'PriceGenerator';

PriceGenerator.defaultProps = { t: key => key };

PriceGenerator.propTypes = {
  price: PropTypes.shape({
    Price: PropTypes.number,
    Currency: PropTypes.string,
    PriceUnit: PropTypes.string,
  }),
};

export default compose(withNamespaces(), withSitecoreContext())(PriceGenerator);
